import { Container, FormControl, MenuItem, Select, Stack, Tab, Tabs } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import copy from 'copy-to-clipboard';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Breadcrumbs from 'commons/Breadcrumbs';
import PaginationCustom from 'commons/PaginationCustom';
import Text from 'commons/Text';
import API from 'configs/API';
import usePagination from 'hooks/usePagination';
import { ReactComponent as FilterAltIcon } from 'images/filter_alt.svg';
import { ReactComponent as FilterSortIcon } from 'images/filter_sort.svg';
import Routes from 'utils/Route';
import { axiosDelete, axiosPatch } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import ExtendShareLinkDialog from './components/ExtendShareLinkDialog';
import RevokeShareLinkDialog from './components/RevokeShareLinkDialog';
import ShareLinkTable from './components/ShareLinkTable';
import useStyles from './styles';

const TAB = {
  MY_PAGE: 'my_page',
  SELECTED_ARTWORKS: 'selected_artworks',
};

const SORT_OPTIONS = [
  { value: '-created_at', label: 'newest_first' },
  { value: 'created_at', label: 'oldest_first' },
  { value: 'title', label: 'title_asc' },
  { value: '-title', label: 'title_desc' },
  { value: '-access_count', label: 'most_accessed' },
];

const FILTER_STATUS_OPTIONS = [
  { value: '', label: 'all_status' },
  { value: 'true', label: 'active' },
  { value: 'false', label: 'inactive' },
];

const ManageShareLink = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isOpenRevoke, setIsOpenRevoke] = useState(false);
  const [selectedShareLink, setSelectedShareLink] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const [isOpenExtend, setIsOpenExtend] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const tab = searchParams.get('tab') || TAB.MY_PAGE;
  const ordering = searchParams.get('ordering') || '-created_at';
  const isActive = searchParams.get('is_active') || '';

  const params = useMemo(() => {
    const p = Object.fromEntries(searchParams);
    p.tab = tab;
    if (ordering) p.ordering = ordering;
    if (isActive) p.is_active = isActive;
    return p;
  }, [searchParams, tab, ordering, isActive]);

  const breadCrumbs = useMemo(
    () => [
      {
        to: Routes.Home,
        text: t('home'),
      },
      {
        text: t('shared_links'),
        active: true,
      },
    ],
    [t],
  );

  const { data, isPending, refetch } = useQuery({
    queryKey: [API.ARTWORK.MANAGE_SHARE_LINKS, params],
  });

  const rows = data?.results || [];

  const { page, totalPage, onPageChange } = usePagination(data?.count, data?.page_size);

  const handleTabChange = useCallback(
    (_, newTab) => {
      const newParams = new URLSearchParams();
      newParams.set('tab', newTab);
      if (ordering && ordering !== '-created_at') newParams.set('ordering', ordering);
      if (isActive) newParams.set('is_active', isActive);
      setSearchParams(newParams);
    },
    [ordering, isActive, setSearchParams],
  );

  const handleSortChange = useCallback(
    (e) => {
      const value = e.target.value;
      searchParams.set('ordering', value);
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleFilterChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value) {
        searchParams.set('is_active', value);
      } else {
        searchParams.delete('is_active');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleCopy = (shareUrl) => {
    if (!shareUrl) return;
    copy(shareUrl);
    ToastTopHelper.success(t('link_copied') || 'Link copied');
  };

  const handleOpenRevoke = useCallback((row) => {
    setSelectedShareLink(row);
    setIsOpenRevoke(true);
  }, []);

  const handleCloseRevoke = useCallback(() => {
    if (isRevoking) return;
    setIsOpenRevoke(false);
    setSelectedShareLink(null);
  }, [isRevoking]);

  const handleConfirmRevoke = useCallback(async () => {
    if (!selectedShareLink?.id) return;

    setIsRevoking(true);
    try {
      const { success, data: updatedData } = await axiosDelete(
        false,
        API.ARTWORK.REVOKE_SHARE_LINK(selectedShareLink.id),
      );

      if (!success) return;

      ToastTopHelper.success(
        updatedData?.message || t('revoked_successfully') || 'Revoked successfully',
      );
      await refetch();
      setIsOpenRevoke(false);
      setSelectedShareLink(null);
    } catch (err) {
      const msg = err?.message || t('revoke_failed') || 'Failed to revoke share link';
      ToastTopHelper.error(msg);
    } finally {
      setIsRevoking(false);
    }
  }, [selectedShareLink, t, refetch]);

  const handleOpenExtend = useCallback((row) => {
    setSelectedShareLink(row);
    setIsOpenExtend(true);
  }, []);

  const handleCloseExtend = useCallback(() => {
    if (isExtending) return;
    setIsOpenExtend(false);
  }, [isExtending]);

  const handleConfirmExtend = useCallback(
    async ({ expiration_type, expires_at }) => {
      if (!selectedShareLink?.id) return;

      setIsExtending(true);
      try {
        const payload = {
          expiration_type,
          expires_at,
          is_active: true,
        };

        const { success, data: updatedData } = await axiosPatch(
          false,
          API.ARTWORK.EXTEND_SHARE_LINK(selectedShareLink.id),
          payload,
        );

        if (!success) return;

        ToastTopHelper.success(
          updatedData?.message || t('extended_successfully') || 'Extended successfully',
        );
        await refetch();
        setIsOpenExtend(false);
      } catch (err) {
        const msg = err?.message || t('extend_failed') || 'Failed to extend share link';
        ToastTopHelper.error(msg);
      } finally {
        setIsExtending(false);
      }
    },
    [selectedShareLink, t, refetch],
  );

  const formatDateTime = (value) => {
    if (!value) return '--';
    const m = moment(value);
    if (!m.isValid()) return '--';
    return m.format('DD/MM/YYYY HH:mm');
  };

  const formatExpiry = (value, expirationType) => {
    if (!value && (expirationType === 'never' || expirationType === 'permanent')) {
      return t('permanent') || 'Permanent';
    }
    if (!value) return t('permanent') || 'Permanent';
    const m = moment(value);
    if (!m.isValid()) return '--';
    return m.format('DD/MM/YYYY HH:mm');
  };

  return (
    <Container className={classes.container}>
      <Breadcrumbs
        items={breadCrumbs}
        breadcrumbsStyle={classes.breadCrumbs}
      />

      <Stack
        direction="row"
        className={classes.headerRow}
      >
        <Text
          type="2xl"
          title
          variant="h1"
          className={classes.heading}
        >
          {t('shared_links')}
        </Text>
      </Stack>

      <Stack
        direction="row"
        className={classes.tabsRow}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          className={classes.tabs}
          TabIndicatorProps={{ className: classes.tabIndicator }}
        >
          <Tab
            value={TAB.MY_PAGE}
            label={t('my_page') || 'My page'}
            className={classes.tab}
          />
          <Tab
            value={TAB.SELECTED_ARTWORKS}
            label={t('selected_artworks') || 'Selected artworks'}
            className={classes.tab}
          />
        </Tabs>

        <div className={classes.filtersRow}>
          <FormControl size="small">
            <Select
              value={ordering}
              onChange={handleSortChange}
              className={classes.smallSelect}
              startAdornment={<FilterSortIcon className={classes.smallIcon} />}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                >
                  {t(opt.label) || opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select
              value={isActive}
              onChange={handleFilterChange}
              displayEmpty
              className={classes.smallSelect}
              startAdornment={<FilterAltIcon className={classes.smallIcon} />}
            >
              {FILTER_STATUS_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                >
                  {t(opt.label) || opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Stack>

      <ShareLinkTable
        rows={rows}
        isPending={isPending}
        onCopy={handleCopy}
        onRevoke={handleOpenRevoke}
        onExtend={handleOpenExtend}
        formatDateTime={formatDateTime}
        formatExpiry={formatExpiry}
      />

      <RevokeShareLinkDialog
        open={isOpenRevoke}
        onClose={handleCloseRevoke}
        onConfirm={handleConfirmRevoke}
        loading={isRevoking}
      />

      <ExtendShareLinkDialog
        open={isOpenExtend}
        onClose={handleCloseExtend}
        onConfirm={handleConfirmExtend}
        loading={isExtending}
        defaultValue={selectedShareLink?.expires_at}
        defaultExpirationType={selectedShareLink?.expiration_type}
      />

      {!!totalPage && (
        <PaginationCustom
          paginationStyle={classes.pagination}
          count={totalPage}
          page={page}
          onChange={onPageChange}
        />
      )}
    </Container>
  );
};

export default ManageShareLink;
